import React, { useState, useEffect } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { media } from "../theme/breakpoints";
import { TODAY_STR } from "../data/mockBookings";
import { fmtINR } from "../utils/formatters";
import _ from "lodash";
import { navigate } from "@reach/router";
import ActionBar from "../../common/ActionBar";
import { Padding } from "../../internal/EsignerTable";
import BookingService from "../../../services/BookingService";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import {
  SegLabel,
  TabStrip,
  TabButton,
  TabLabel,
  TabActiveBar,
  FieldBlock,
  FloatLabel,
} from "../WalkinBooking/Styles";
import { CustomDatePicker } from "../../common/FormInputs";
import moment from "moment";
import CustomSelect from "../../common/CustomSelect/CustomSelect";
import queryString from "query-string";
import { PrimaryOutlinedCTAButton } from "../../common/Buttons";

const ReportsPage = () => {
  const toDateMoment = (v) => (v ? moment(v, "YYYY-MM-DD") : null);
  const thisMonth = new Date(TODAY_STR);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState();

  const [filterType, setFilterType] = useState("day");
  const [selectedDate, setSelectedDate] = useState(TODAY_STR.split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(thisMonth.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(thisMonth.getFullYear());
  const [selectedFY, setSelectedFY] = useState(2025);

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "booking_dashboard":
        navigate("/dashboard/case-manager/booking-dashboard");
        break;
      default:
        break;
    }
  };

  const fetchReports = async () => {
    try {
      setLoader({ state: true, message: "Fetching reports..." });
      let params = { filter: filterType };
      if (filterType === "day") {
        params.date = selectedDate;
      } else if (filterType === "month") {
        params.month = selectedMonth;
        params.year = selectedYear;
      } else if (filterType === "year") {
        params.fy = selectedFY;
      }
      let stringParams = "";
      if (!_.isEmpty(params)) {
        stringParams = `?${queryString.stringify(params)}`;
      }
      const response = await BookingService.fetchReportsDashboard(stringParams);
      setState(response);
    } catch (error) {
      enqueueSnackbar(error?.message || "Failed to fetch reports", {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoader({ state: true, message: "Generating Excel..." });

      let params = {
        filter: filterType,
        export: "excel",
      };

      if (filterType === "day") {
        params.date = selectedDate;
      } else if (filterType === "month") {
        params.month = selectedMonth;
        params.year = selectedYear;
      } else if (filterType === "year") {
        params.fy = selectedFY;
      }

      const stringParams = `?${queryString.stringify(params)}`;

      const response = await BookingService.fetchReportsDashboard(stringParams);

      if (response?.excelUrl || response) {
        const link = document.createElement("a");
        const fileUrl = response?.url || response?.excelUrl;
        const fileName =
          fileUrl?.split("/").pop().split("?")[0] ||
          `reports_${filterType}_${Date.now()}.xlsx`;
        link.href = fileUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        enqueueSnackbar("Export link not received from server", {
          variant: "warning",
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "Failed to export Excel", {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  useEffect(() => {
    fetchReports(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, selectedDate, selectedMonth, selectedYear, selectedFY]);

  const FILTER_OPTIONS = [
    { id: "day", label: "Day" },
    { id: "month", label: "Month" },
    { id: "year", label: "Financial Year" },
  ];

  return (
    <>
      <ActionBar
        breadcrumbs={["Booking Dashboard", "Reports"]}
        {...{ onBreadcrumbClick }}
      />
      <Padding>
        <PageWrap>
          <HeaderRow>
            <TitleBlock>
              <PageTitle>Reports</PageTitle>
            </TitleBlock>
            <Exports>
              <PrimaryOutlinedCTAButton
                style={{
                  padding: "10px 10px",
                  borderRadius: 8,
                }}
                onClick={handleExportExcel}
              >
                Export XLSX
              </PrimaryOutlinedCTAButton>
            </Exports>
          </HeaderRow>

          <FilterSection>
            <SegLabel>Reporting Period</SegLabel>
            <TabStrip>
              {FILTER_OPTIONS.map((option) => {
                const active = filterType === option.id;
                return (
                  <TabButton
                    key={option.id}
                    type="button"
                    $active={active}
                    onClick={() => setFilterType(option.id)}
                  >
                    <TabLabel $active={active}>{option.label}</TabLabel>
                    <TabActiveBar $active={active} />
                  </TabButton>
                );
              })}
            </TabStrip>

            {/* Secondary Filters */}
            {filterType === "day" && (
              <FieldBlock>
                <FloatLabel>Date</FloatLabel>
                <CustomDatePicker
                  value={toDateMoment(selectedDate)}
                  onChange={(momentVal) =>
                    setSelectedDate(momentVal.format("YYYY-MM-DD"))
                  }
                  format="DD/MM/YYYY"
                  /*
                  maxDate={new Date()}
                  minDate={moment()}
                  */
                  clearable
                  inputVariant="outlined"
                  className="input-white"
                  style={{ marginTop: 6, width: "33%" }}
                  emptyLabel="DD/MM/YYYY"
                />
              </FieldBlock>
            )}

            {filterType === "month" && (
              <FieldBlock>
                <GridContainer>
                  <FieldItem>
                    <FloatLabel>Month</FloatLabel>
                    <CustomSelect
                      white
                      name={"month"}
                      value={selectedMonth || ""}
                      onChange={(e) => setSelectedMonth(Number(e.target.value))}
                      menuItemValues={Array.from({ length: 12 }, (_, i) => ({
                        value: i + 1,
                        label: new Date(2026, i).toLocaleString("default", {
                          month: "long",
                        }),
                      }))}
                    />
                  </FieldItem>
                  <FieldItem>
                    <FloatLabel>Year</FloatLabel>
                    <CustomSelect
                      white
                      name={"year"}
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                      menuItemValues={Array.from({ length: 5 }, (_, i) => ({
                        value: 2024 + i,
                        label: 2024 + i,
                      }))}
                    />
                  </FieldItem>
                </GridContainer>
              </FieldBlock>
            )}

            {filterType === "year" && (
              <FieldBlock>
                <FloatLabel>Financial Year</FloatLabel>
                <CustomSelect
                  white
                  name={"fy"}
                  style={{ width: "33%" }}
                  value={selectedFY}
                  onChange={(e) => setSelectedFY(Number(e.target.value))}
                  menuItemValues={Array.from({ length: 6 }, (_, i) => {
                    const year = 2023 + i;

                    return {
                      value: year,
                      label: `FY ${year} (Apr ${year} - Mar ${year + 1})`,
                    };
                  })}
                />
              </FieldBlock>
            )}
          </FilterSection>

          <StatsGrid>
            <Card>
              <StatLabel>Booking Revenue</StatLabel>
              <StatValue $color={COLORS.COLOR_DARK}>
                {fmtINR(state?.summary?.bookingRevenue?.amount)}
              </StatValue>
              <StatSub>
                {state?.summary?.bookingRevenue?.bookings} bookings
              </StatSub>
            </Card>
            <Card>
              <StatLabel>On-Site Revenue</StatLabel>
              <StatValue $color="#c98d18">
                {fmtINR(state?.summary?.onsiteRevenue?.amount)}
              </StatValue>
              <StatSub>Supplementary invoices</StatSub>
            </Card>
            <Card>
              <StatLabel>Total Revenue</StatLabel>
              <StatValue $color={COLORS.BTN_GREEN}>
                {fmtINR(state?.summary?.totalRevenue?.amount)}
              </StatValue>
              <StatSub>Booking + on-site</StatSub>
            </Card>
            <Card>
              <StatLabel>Refunds Issued</StatLabel>
              <StatValue $color={COLORS.REMOVE_RED}>
                {fmtINR(state?.summary?.refundsIssued?.amount)}
              </StatValue>
              <StatSub>Cancellations</StatSub>
            </Card>
          </StatsGrid>
          {/* Room Utilisation Card */}
          <Card $padding={0}>
            <RoomHeader>
              <RoomTitle>Room Utilisation</RoomTitle>
            </RoomHeader>

            <TableWrap>
              <Table>
                <Thead>
                  <tr>
                    <Th>Room</Th>
                    <Th $align="right">Bookings</Th>
                    <Th $align="right">Revenue</Th>
                    <Th>Utilisation</Th>
                  </tr>
                </Thead>
                <tbody>
                  {state?.roomUtilisation?.map((room) => (
                    <Tr key={room.roomCode}>
                      <Td>
                        <RoomName>{room.roomName}</RoomName>
                        <RoomMeta>
                          Cap. {room.capacity} {room.size && `· ${room.size}`}
                        </RoomMeta>
                      </Td>
                      <Td $align="right">
                        <span
                          style={{
                            fontFamily: theme.fonts.primaryFontBold,
                            fontWeight: 700,
                            color: COLORS.COLOR_DARK,
                            fontSize: 14,
                          }}
                        >
                          {room.bookings}
                        </span>
                      </Td>
                      <Td $align="right">
                        <span
                          style={{
                            fontFamily: theme.fonts.primaryFontBold,
                            fontWeight: 700,
                            color: COLORS.BTN_GREEN,
                          }}
                        >
                          {fmtINR(room.revenue)}
                        </span>
                      </Td>
                      <Td style={{ width: 260 }}>
                        <ProgressBar>
                          <BarTrack>
                            <BarFill $pct={room.utilisation} />
                          </BarTrack>
                          <PctText>{room.utilisation}%</PctText>
                        </ProgressBar>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            {/* Mobile Card View */}
            <RoomCards>
              {state?.roomUtilisation?.map((room) => (
                <RoomCard key={room.roomCode}>
                  <RCRow>
                    <RoomName>{room.roomName}</RoomName>
                    <span
                      style={{
                        fontFamily: theme.fonts.primaryFontBold,
                        fontWeight: 700,
                        color: COLORS.BTN_GREEN,
                        fontSize: 14,
                      }}
                    >
                      {fmtINR(room.revenue)}
                    </span>
                  </RCRow>

                  <RCStats>
                    <span>
                      {room.bookings} booking{room.bookings !== 1 ? "s" : ""}
                    </span>
                    <span>Cap. {room.capacity}</span>
                    {room.size && <span>{room.size}</span>}
                  </RCStats>

                  <ProgressBar>
                    <BarTrack>
                      <BarFill $pct={room.utilisation} />
                    </BarTrack>
                    <PctText>{room.utilisation}%</PctText>
                  </ProgressBar>
                </RoomCard>
              ))}
            </RoomCards>
          </Card>
        </PageWrap>
      </Padding>
    </>
  );
};

export default ReportsPage;

const Card = styled.div`
  background: ${COLORS.PRIMARY_WHITE};
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 10px;
  padding: ${({ $padding }) =>
    $padding !== undefined ? `${$padding}px` : "22px"};
`;

const PageWrap = styled.div`
  padding: 16px 16px;
  max-width: 1360px;
  margin: 0 auto;
  animation: fadeIn 0.25s ease-out;

  ${media.tablet} {
    padding: 20px 24px;
  }
  ${media.laptop} {
    padding: 24px 28px;
  }
`;

const HeaderRow = styled.div`
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${media.tablet} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const TitleBlock = styled.div``;

const PageTitle = styled.h2`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  margin: 0 0 4px;

  ${media.tablet} {
    font-size: 20px;
  }
`;

const Exports = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 18px;

  ${media.mobile} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${media.tablet} {
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
  }
`;

const StatLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  color: #8e99a8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 18px;
  font-weight: 800;
  color: ${({ $color }) => $color};

  ${media.tablet} {
    font-size: 20px;
  }
`;

const StatSub = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: #5c6b7f;
  margin-top: 4px;
`;

const RoomHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};

  ${media.tablet} {
    padding: 16px 22px;
  }
`;

const RoomTitle = styled.h3`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 14.5px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  margin: 0;
`;

// Table (tablet+)
const TableWrap = styled.div`
  display: none;
  ${media.tablet} {
    display: block;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12.5px;
`;

const Thead = styled.thead`
  background: ${COLORS.TABLE_GREY_CELL};
`;

const Th = styled.th`
  padding: 11px 16px;
  text-align: ${({ $align }) => $align || "left"};
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 10.5px;
  font-weight: 700;
  color: #5c6b7f;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Td = styled.td`
  padding: 14px 16px;
  vertical-align: middle;
  text-align: ${({ $align }) => $align || "left"};
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${COLORS.HEADER_BORDER};
`;

const RoomName = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
`;

const RoomMeta = styled.div`
  font-size: 11px;
  color: #8e99a8;
  margin-top: 2px;
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: #f0f4f8;
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div`
  width: ${({ $pct }) => $pct}%;
  height: 100%;
  background: linear-gradient(90deg, ${COLORS.BTN_GREEN}, #006670);
  border-radius: 4px;
`;

const PctText = styled.span`
  font-size: 11px;
  color: #5c6b7f;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-weight: 600;
  min-width: 30px;
  text-align: right;
`;

// Mobile cards
const RoomCards = styled.div`
  display: block;
  padding: 12px;

  ${media.tablet} {
    display: none;
  }
`;

const RoomCard = styled.div`
  background: ${COLORS.PRIMARY_WHITE};
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 10px;
`;

const RCRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const RCStats = styled.div`
  display: flex;
  gap: 16px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11.5px;
  color: #5c6b7f;
  margin: 8px 0;
`;

const FilterSection = styled.div`
  margin-bottom: 24px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
